import { ROUTE_PATHS } from "@/routes/routePaths";
import { getCorridorPageSeo } from "@/config/corridorSeoMeta";
import { PAGE_SEO } from "@/config/pageSeo";
import { PUBLIC_SITE_URL } from "@/config/site";
import {
  DURI_ORG_ID,
  DURI_WEBSITE_ID,
  buildOrganizationNode,
  buildWebSiteNode,
} from "@/config/organizationSchema";
import { faqCategories } from "@/data/faqs";
import { GUIDE_ARTICLES } from "@/data/content/guides";
import { getCorridorBySlug, POPULAR_ROUTE_SLUGS } from "@/data/corridors/registry";
import { toPublicCorridorSlug } from "@/data/corridors/corridorSeoSlugs";

type Crumb = { name: string; path: string };

const SKIP_SCHEMA_PREFIXES = [
  "/dashboard",
  "/account",
  "/delete-account",
  "/webview-init",
  "/privacypolicy",
  "/TermsofServices",
  "/TermsAndConditions",
  "/CancellationPolicy",
  "/DataProcessingandAgreement",
  "/verificationprocess",
  "/dev-team",
];

function abs(path: string): string {
  return `${PUBLIC_SITE_URL}${path === "/" ? "/" : path}`;
}

function buildBreadcrumbList(crumbs: Crumb[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: abs(c.path),
    })),
  };
}

function buildWebPage(pathNorm: string, name: string, description?: string) {
  return {
    "@type": "WebPage",
    "@id": `${abs(pathNorm)}#webpage`,
    url: abs(pathNorm),
    name,
    ...(description ? { description } : {}),
    inLanguage: "en-IN",
    isPartOf: { "@id": DURI_WEBSITE_ID },
    publisher: { "@id": DURI_ORG_ID },
  };
}

function pageMeta(pathNorm: string): { title: string; description: string } | undefined {
  if (PAGE_SEO[pathNorm]) return PAGE_SEO[pathNorm];
  const guideMatch = pathNorm.match(/^\/guides\/([^/]+)$/);
  if (guideMatch) {
    const guide = GUIDE_ARTICLES[guideMatch[1]];
    if (guide) return { title: guide.title, description: guide.description };
  }
  const corridorMatch = pathNorm.match(/^\/routes\/([^/]+)$/);
  if (corridorMatch) {
    return getCorridorPageSeo(corridorMatch[1]);
  }
  return undefined;
}

/** Breadcrumb trails for marketing pages (path → crumbs). */
const BREADCRUMB_MAP: Record<string, Crumb[]> = {
  [ROUTE_PATHS.HOW_DURI_WORKS]: [
    { name: "Home", path: "/" },
    { name: "How Duri Works", path: ROUTE_PATHS.HOW_DURI_WORKS },
  ],
  [ROUTE_PATHS.FIND_RIDES]: [
    { name: "Home", path: "/" },
    { name: "Available Rides", path: ROUTE_PATHS.FIND_RIDES },
  ],
  [ROUTE_PATHS.VERIFICATION_PROCESS]: [
    { name: "Home", path: "/" },
    { name: "Verification Process", path: ROUTE_PATHS.VERIFICATION_PROCESS },
  ],
  [ROUTE_PATHS.OFFER_A_RIDE]: [
    { name: "Home", path: "/" },
    { name: "Offer a Ride", path: ROUTE_PATHS.OFFER_A_RIDE },
  ],
  [ROUTE_PATHS.RIDE_REQUESTS]: [
    { name: "Home", path: "/" },
    { name: "Ride Requests", path: ROUTE_PATHS.RIDE_REQUESTS },
  ],
  [ROUTE_PATHS.DAILY_COMMUTE_CARPOOL]: [
    { name: "Home", path: "/" },
    { name: "Daily Commute Carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
  ],
  [ROUTE_PATHS.CORPORATE_CARPOOL]: [
    { name: "Home", path: "/" },
    { name: "Corporate Carpool", path: ROUTE_PATHS.CORPORATE_CARPOOL },
  ],
  [ROUTE_PATHS.HYDERABAD_DAILY_COMMUTE_CARPOOL]: [
    { name: "Home", path: "/" },
    { name: "Daily Commute Carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
    { name: "Hyderabad Daily Commute", path: ROUTE_PATHS.HYDERABAD_DAILY_COMMUTE_CARPOOL },
  ],
  [ROUTE_PATHS.POPULAR_ROUTES]: [
    { name: "Home", path: "/" },
    { name: "Popular Rides", path: ROUTE_PATHS.POPULAR_ROUTES },
  ],
  [ROUTE_PATHS.SAFETY]: [
    { name: "Home", path: "/" },
    { name: "Safety", path: ROUTE_PATHS.SAFETY },
  ],
  [ROUTE_PATHS.ABOUT_DURI]: [
    { name: "Home", path: "/" },
    { name: "About Duri", path: ROUTE_PATHS.ABOUT_DURI },
  ],
  [ROUTE_PATHS.NEWS]: [
    { name: "Home", path: "/" },
    { name: "News & Updates", path: ROUTE_PATHS.NEWS },
  ],
  [ROUTE_PATHS.RIDESHARING_BENEFITS]: [
    { name: "Home", path: "/" },
    { name: "Ridesharing Benefits", path: ROUTE_PATHS.RIDESHARING_BENEFITS },
  ],
  [ROUTE_PATHS.RIDESHARING_IMPACT]: [
    { name: "Home", path: "/" },
    { name: "Ridesharing Impact", path: ROUTE_PATHS.RIDESHARING_IMPACT },
  ],
  [ROUTE_PATHS.HELP_CENTER]: [
    { name: "Home", path: "/" },
    { name: "Help Center", path: ROUTE_PATHS.HELP_CENTER },
  ],
  [ROUTE_PATHS.FAQS]: [
    { name: "Home", path: "/" },
    { name: "FAQs", path: ROUTE_PATHS.FAQS },
  ],
  [ROUTE_PATHS.HYDERABAD_CARPOOL]: [
    { name: "Home", path: "/" },
    { name: "Hyderabad Carpool", path: ROUTE_PATHS.HYDERABAD_CARPOOL },
  ],
  [ROUTE_PATHS.VIJAYAWADA_RIDESHARING]: [
    { name: "Home", path: "/" },
    { name: "Vijayawada Ridesharing", path: ROUTE_PATHS.VIJAYAWADA_RIDESHARING },
  ],
  [ROUTE_PATHS.BANGALORE_CARPOOL]: [
    { name: "Home", path: "/" },
    { name: "Bangalore Carpool", path: ROUTE_PATHS.BANGALORE_CARPOOL },
  ],
  [ROUTE_PATHS.CHENNAI_RIDESHARING]: [
    { name: "Home", path: "/" },
    { name: "Chennai Ridesharing", path: ROUTE_PATHS.CHENNAI_RIDESHARING },
  ],
  [ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA]: [
    { name: "Home", path: "/" },
    { name: "Intercity Ridesharing India", path: ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA },
  ],
  [ROUTE_PATHS.VIJAYAWADA_DAILY_COMMUTE_CARPOOL]: [
    { name: "Home", path: "/" },
    { name: "Daily Commute Carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
    { name: "Vijayawada Daily Commute", path: ROUTE_PATHS.VIJAYAWADA_DAILY_COMMUTE_CARPOOL },
  ],
};

function buildFaqPageSchema() {
  return {
    "@type": "FAQPage",
    mainEntity: faqCategories.flatMap((cat) =>
      cat.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.navigation
            ? `${f.answer} Navigation: ${f.navigation.join(" → ")}.`
            : f.answer,
        },
      })),
    ),
  };
}

function buildPopularRoutesItemList() {
  return {
    "@type": "ItemList",
    name: "Popular carpool routes on Duri",
    description: "Intercity carpool and ridesharing routes across India on Duri.",
    numberOfItems: POPULAR_ROUTE_SLUGS.length,
    itemListElement: POPULAR_ROUTE_SLUGS.map((slug, index) => {
      const corridor = getCorridorBySlug(slug);
      const publicSlug = toPublicCorridorSlug(slug);
      const path = `${ROUTE_PATHS.INTERCITY_ROUTES}/${publicSlug}`;
      const name = corridor
        ? `${corridor.cityA} to ${corridor.cityB} Carpool`
        : publicSlug.replace(/-/g, " ");
      return {
        "@type": "ListItem",
        position: index + 1,
        name,
        url: abs(path),
      };
    }),
  };
}

function buildCorridorGraph(pathNorm: string, publicSlug: string): unknown[] {
  const corridor = getCorridorBySlug(publicSlug);
  if (!corridor) return [];

  const meta = getCorridorPageSeo(publicSlug);
  const title =
    meta?.title ?? `${corridor.cityA} to ${corridor.cityB} Carpool & Ride Sharing | Duri`;
  const description =
    meta?.description ??
    `Book a carpool from ${corridor.cityA} to ${corridor.cityB} on Duri. Verified co-travellers and daily rides.`;

  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "Intercity Ridesharing India", path: ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA },
    { name: `${corridor.cityA} → ${corridor.cityB}`, path: pathNorm },
  ];

  return [
    buildOrganizationNode(),
    buildWebPage(pathNorm, title, description),
    {
      "@type": "TravelAction",
      name: `Carpool from ${corridor.cityA} to ${corridor.cityB}`,
      fromLocation: { "@type": "City", name: corridor.cityA },
      toLocation: { "@type": "City", name: corridor.cityB },
    },
    {
      "@type": "TravelAction",
      name: `Carpool from ${corridor.cityB} to ${corridor.cityA}`,
      fromLocation: { "@type": "City", name: corridor.cityB },
      toLocation: { "@type": "City", name: corridor.cityA },
    },
    {
      "@type": "FAQPage",
      mainEntity: corridor.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    buildBreadcrumbList(crumbs),
  ];
}

function buildGuideGraph(pathNorm: string, guideSlug: string): unknown[] {
  const guide = GUIDE_ARTICLES[guideSlug];
  if (!guide) return [];

  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: guide.title, path: pathNorm },
  ];

  return [
    buildOrganizationNode(),
    {
      "@type": "Article",
      "@id": `${abs(pathNorm)}#article`,
      headline: guide.title,
      description: guide.description,
      url: abs(pathNorm),
      inLanguage: "en-IN",
      publisher: { "@id": DURI_ORG_ID },
      isPartOf: { "@id": DURI_WEBSITE_ID },
    },
    buildBreadcrumbList(crumbs),
  ];
}

function buildNewsGraph(pathNorm: string): unknown[] {
  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "News & Updates", path: ROUTE_PATHS.NEWS },
  ];

  return [
    buildOrganizationNode(),
    buildWebSiteNode(),
    buildBreadcrumbList(crumbs),
    {
      "@type": "NewsArticle",
      "@id": `${abs(pathNorm)}#newsarticle`,
      headline:
        "Mohan Nanduri Addresses KR Bharat Conclave 2026: Redefining Mobility, Trust, and India’s Sharing Economy",
      description:
        "Speaking before industry leaders, policymakers, and innovators at the KR Bharat Conclave 2026 at T-Hub Hyderabad, Mohan Nanduri, CEO of Duri App and Founder & CTO of HansAI Technosoft, delivered a keynote on transforming Indian transportation through verified ridesharing and sustainable green mobility.",
      image: [`${PUBLIC_SITE_URL}/images/kr-bharat-conclave-2026.webp`],
      datePublished: "2026-07-26T10:00:00+05:30",
      dateModified: "2026-07-26T10:00:00+05:30",
      inLanguage: "en-IN",
      mainEntityOfPage: abs(pathNorm),
      author: {
        "@type": "Person",
        name: "Mohan Nanduri",
        jobTitle: "Founder & CTO of HansAI | CEO of Duri App",
        worksFor: {
          "@type": "Organization",
          name: "Duri Ridesharing",
          url: PUBLIC_SITE_URL,
        },
      },
      publisher: { "@id": DURI_ORG_ID },
      isPartOf: { "@id": DURI_WEBSITE_ID },
      about: [
        { "@type": "Thing", name: "Ridesharing" },
        { "@type": "Thing", name: "KR Bharat Conclave 2026" },
        { "@type": "Thing", name: "Vikasit Bharat 2047" },
        { "@type": "Thing", name: "Sustainable Mobility" },
        { "@type": "Thing", name: "Carpooling in India" },
      ],
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h2", "article p"],
      },
    },
    {
      "@type": "VideoObject",
      "@id": `${abs(pathNorm)}#video`,
      name: "Mohan Nanduri Keynote Address | KR Bharat Conclave 2026",
      description:
        "Official keynote address by Mohan Nanduri, CEO of Duri App, at KR Bharat Conclave 2026 at T-Hub Hyderabad on redefining mobility, trust, and India's sharing economy.",
      thumbnailUrl: [`${PUBLIC_SITE_URL}/images/kr-bharat-conclave-2026.webp`],
      uploadDate: "2026-07-26T10:00:00+05:30",
      duration: "PT20M22S",
      contentUrl: "https://www.youtube.com/watch?v=RnawmTffczc",
      embedUrl: "https://www.youtube-nocookie.com/embed/RnawmTffczc",
      publisher: { "@id": DURI_ORG_ID },
      inLanguage: "en-IN",
    },
    buildWebPage(
      pathNorm,
      "Mohan Nanduri Keynote at KR Bharat Conclave 2026 | Duri News",
      "Watch Mohan Nanduri's keynote address at KR Bharat Conclave 2026 on redefining Indian mobility, live Vahan & DigiLocker trust, and sustainable carpooling under Vikasit Bharat 2047."
    ),
  ];
}

export function shouldSkipStructuredData(pathname: string): boolean {
  const pathNorm = pathname.replace(/\/$/, "") || "/";
  if (pathNorm === "/") return false;
  return SKIP_SCHEMA_PREFIXES.some((p) => pathNorm.startsWith(p));
}

/**
 * Build schema.org @graph nodes for the current marketing route.
 */
export function buildStructuredDataGraph(pathname: string): unknown[] | null {
  const pathNorm = pathname.replace(/\/$/, "") || "/";

  if (shouldSkipStructuredData(pathname)) return null;

  // —— Homepage ——
  if (pathNorm === "/") {
    const meta = PAGE_SEO["/"];
    return [
      buildOrganizationNode(),
      buildWebSiteNode(),
      buildWebPage("/", meta.title, meta.description),
    ];
  }

  // —— Corridor detail ——
  const corridorMatch = pathNorm.match(/^\/routes\/([^/]+)$/);
  if (corridorMatch) {
    return buildCorridorGraph(pathNorm, corridorMatch[1]);
  }

  // —— Guide articles ——
  const guideMatch = pathNorm.match(/^\/guides\/([^/]+)$/);
  if (guideMatch) {
    return buildGuideGraph(pathNorm, guideMatch[1]);
  }

  // —— News articles, keynote video & rich snippets ——
  if (pathNorm === ROUTE_PATHS.NEWS) {
    return buildNewsGraph(pathNorm);
  }

  const graph: unknown[] = [buildOrganizationNode()];

  const crumbs = BREADCRUMB_MAP[pathNorm];
  const meta = pageMeta(pathNorm);

  if (crumbs) {
    graph.push(buildBreadcrumbList(crumbs));
    const pageName = meta?.title ?? crumbs[crumbs.length - 1].name;
    graph.push(buildWebPage(pathNorm, pageName, meta?.description));
  }

  // —— Help Center: full Organization with ContactPoint ——
  if (pathNorm === ROUTE_PATHS.HELP_CENTER) {
    graph[0] = buildOrganizationNode({ includeContact: true });
  }

  // —— Popular Routes: ItemList ——
  if (pathNorm === ROUTE_PATHS.POPULAR_ROUTES) {
    graph.push(buildPopularRoutesItemList());
  }

  // —— FAQs: FAQPage (machine readability; no SERP rich result expected) ——
  if (pathNorm === ROUTE_PATHS.FAQS) {
    graph.push(buildFaqPageSchema());
  }

  if (graph.length <= 1 && !crumbs) return null;

  return graph;
}
